import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { withFormik } from 'formik';
import styled, { css } from 'styled-components';
import useTranslation from '@src/hooks/useTranslation';

import { getUserProfile } from '@src/store/selectors';
import { TSideEffectState } from 'src/types/common';
import { STATE_LOADING, STATE_SUCCESS } from '@src/const/common';

import Button from '@src/components/common/button';
import { Avatar, BlankAvatar } from './styled';

const ButtonGroup = styled.div`
	display: flex;
	justify-content: flex-end;
	margin-top: ${props => (props.buttonPosition === 'right' ? 0 : 12)}px;
	margin-left: ${props => (props.buttonPosition === 'right' ? 12 : 0)}px;
	width: ${props => (props.buttonPosition === 'right' ? 'auto' : '100%')};

	& > *:not(:last-child) {
		margin-right: 8px;
	}

	@media ${props => props.theme.device.smTablet} {
		margin-top: 12px;
		margin-left: auto;
	}

	@media ${props => props.theme.device.mobile} {
		margin-top: 12px;
		margin-left: auto;
	}
`;

const buttonCommonStyle = css`
	padding: 8px 24px;
	min-width: 100px;

	@media ${props => props.theme.device.mobile} {
		flex: 1 1 auto;
	}
`;

const SubmitButton = styled(Button)`
	${buttonCommonStyle}
`;

const CancelButton = styled(Button)`
	${buttonCommonStyle}
	color: ${props => props.theme.colors.primary};
	border: 1px solid ${props => props.theme.colors.primary};
	background: #FFFFFF;
`;

const InputMessageWrapper = styled.div`
	display: flex;
	background: #FFFFFF;
	border-radius: 4px;
`;

const Content = styled.div`
	flex: 1 1 auto;
	display: flex;
	align-items: center;
	flex-direction: ${props => (props.buttonPosition === 'right' ? 'row' : 'column')};

	@media ${props => props.theme.device.smTablet} {
		flex-direction: column;
	}

	@media ${props => props.theme.device.mobile} {
		flex-direction: column;
	}
`;

const inputPlaceHolderStyle = css`
	::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
		color: #676767;
		opacity: 1; /* Firefox */
		font-weight: 300;
	}

	:-ms-input-placeholder { /* Internet Explorer 10-11 */
		color: #676767;
		font-weight: 300;
	}

	::-ms-input-placeholder { /* Microsoft Edge */
		color: #676767;
		font-weight: 300;
	}
`;

const Textarea = styled.textarea`
	width: 100%;
	border: 1px solid #DDDDDD;
	border-radius: 4px;
	font-size: 14px;
	padding: 10px 12px;
	color: #333333;
	font-weight: 400;
	${inputPlaceHolderStyle}

	&:focus {
		outline-color: ${props => props.theme.colors.primary};
	}
`;

interface IFormValues {
	message: string;
}

interface IInputMessage {
	values?: IFormValues;
	dirty?: boolean;
	isValid?: boolean;
	isSubmitting?: boolean;
	className?: string;
	isReplier?: boolean;
	rows: number;
	addMessageState: TSideEffectState;
	buttonPosition?: string;
	handleChange?: () => void;
	handleSubmit?: () => void;
	/* eslint-disable react/no-unused-prop-types */
	handleMessageSubmit: (message: string) => void;
	/* eslint-enable react/no-unused-prop-types */
	handleOnFocus?: () => void;
	handleOnCancel?: () => void;
	resetForm?: () => void;
}

const InputMessage = ({
	className, isReplier = false, rows = 1, handleOnFocus, buttonPosition, handleOnCancel, addMessageState,
	values, dirty, isValid, isSubmitting, handleChange, handleSubmit, resetForm,
}: IInputMessage): JSX.Element => {
	const disabledSubmit = !dirty || !isValid || isSubmitting;
	const { t } = useTranslation();
	const { message } = values;
	const isLoading = addMessageState === STATE_LOADING;
	const isSuccess = addMessageState === STATE_SUCCESS;
	const profile = useSelector(getUserProfile) as any;
	const link = profile?.profile_picture_link?.link;

	useEffect(() => {
		/**
		 * TODO: Move `resetForm` to formik `handleSubmit` if we can get response success state in `handleSubmit`.
		*/
		if (isSuccess) {
			resetForm();
		}
	}, [isSuccess, resetForm]);

	return (
		<InputMessageWrapper className={className} isReplier={isReplier}>
			{link ? (
				<Avatar src={link} alt="" isReplier={isReplier} />
			) : (<BlankAvatar width={isReplier ? 40 : 60} height={isReplier ? 40 : 60} />)}
			<Content buttonPosition={buttonPosition}>
				<Textarea
					name="message"
					rows={rows}
					value={message}
					placeholder={t('course:discussions:please_input_message')}
					onChange={handleChange}
					onFocus={handleOnFocus}
				/>
				<ButtonGroup buttonPosition={buttonPosition}>
					{handleOnCancel && (
						<CancelButton handleClick={handleOnCancel}>
							{t('course:discussions:cancel')}
						</CancelButton>
					)}
					<SubmitButton
						handleClick={handleSubmit}
						isLoading={isLoading}
						isDisable={disabledSubmit}
					>
						{t('course:discussions:submit_comment')}
					</SubmitButton>
				</ButtonGroup>
			</Content>
		</InputMessageWrapper>
	);
};

export default withFormik({
	mapPropsToValues: () => ({
		message: '',
	}),
	validate: ({ message }) => {
		const errors = {};

		/* eslint-disable dot-notation */
		if (!message.trim()) {
			errors['message'] = true;
		}
		/* eslint-enable dot-notation */

		return errors;
	},
	handleSubmit: ({ message }, { props }: {
		/**
		 * TODO: Hope type check of props in formik could be simplified.
		*/
		props: IInputMessage
	}) => {
		const { handleMessageSubmit } = props;
		handleMessageSubmit(message);
	},
})(InputMessage);
